/* ============================================================
   sc-cart.js — unified basket drawer
   ============================================================
   Merges TWO item sources into a single drawer:

   1. SHOPIFY CART — bed linen, Portmeirion, tableware, etc.
      Read via GET /cart.js, written via POST /cart/change.js.
      Prices come from Shopify variants: always correct.

   2. CONFIGURED SOFAS — bespoke sofa builds from the configurator.
      Stored in localStorage ('sc_sofa_basket') because Shopify's
      cart cannot carry custom prices. The drawer renders the
      computed price from the stored entry.

   The drawer shows both seamlessly. The checkout button behaviour
   depends on what is in the basket:
     - Regular items only → /cart (standard Shopify checkout).
     - Configured sofas present → POST to the draft-order endpoint,
       which creates a Shopify order at the correct prices and
       returns a checkout URL.

   sc-shopify-cart.js owns the Shopify add-to-cart call
   (POST /cart/add.js) and the Vercel pricing API; both files
   write the badge from the same unified count.
   ============================================================ */

(function() {
  if (typeof window === 'undefined') return;
  window.SC = window.SC || {};
  SC.cart = SC.cart || {};

  /* ── Shopify cart cache ── */
  var _shopCart = null;

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function(cart) {
        _shopCart = cart;
        updateCartBadge();
        broadcast();
        return cart;
      });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function(r) {
        if (!r.ok) throw new Error('cart/change.js ' + r.status);
        return r.json();
      })
      .then(function(cart) {
        _shopCart = cart;
        updateCartBadge();
        broadcast();
        return cart;
      });
  }

  function broadcast() {
    try {
      document.dispatchEvent(new CustomEvent('sc:cart-changed', {
        detail: { count: getTotalCount() }
      }));
    } catch (e) { /* older browsers without CustomEvent constructor */ }
  }

  /* ── Configured sofas (localStorage) ── */
  var SOFA_KEY = 'sc_sofa_basket';

  function readSofas() {
    try { return JSON.parse(localStorage.getItem(SOFA_KEY)) || []; }
    catch (e) { return []; }
  }

  function writeSofas(items) {
    localStorage.setItem(SOFA_KEY, JSON.stringify(items));
    updateCartBadge();
    broadcast();
  }

  SC.cart._addConfiguredSofa = function(entry) {
    var items = readSofas();
    items.push(entry);
    writeSofas(items);
  };

  SC.cart._removeConfiguredSofa = function(id) {
    writeSofas(readSofas().filter(function(s) { return s.id !== id; }));
  };

  /* ── Unified counts and money ── */
  function cartCurrency() {
    return (_shopCart && _shopCart.currency === 'USD') ? '$' : '\u00a3';
  }

  function getTotalCount() {
    var shopify = _shopCart ? _shopCart.item_count : 0;
    var sofas = readSofas().length;
    return shopify + sofas;
  }

  function money(pence) {
    var sym = cartCurrency();
    var v = (pence || 0) / 100;
    var opts = (v % 1 === 0)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return sym + v.toLocaleString('en-GB', opts);
  }

  function moneyWhole(pounds) {
    var sym = cartCurrency();
    if (pounds == null) return '';
    var opts = (pounds % 1 === 0)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return sym + pounds.toLocaleString('en-GB', opts);
  }

  /* ── Public API ── */
  SC.cart.refresh = fetchCart;
  SC.cart.getCount = getTotalCount;
  SC.cart.getSubtotal = function() {
    var shopPence = _shopCart ? _shopCart.items_subtotal_price : 0;
    var sofaPounds = readSofas().reduce(function(s, i) { return s + (i.price || 0); }, 0);
    return (shopPence / 100) + sofaPounds;
  };

  /* ── Legacy localStorage layer (old sc_cart key, for unmounted
       bed-linen/Portmeirion components in sc-components.js) ── */
  var LEGACY_KEY = 'sc_cart';
  function readLegacy() {
    try { return JSON.parse(localStorage.getItem(LEGACY_KEY)) || []; }
    catch (e) { return []; }
  }
  function writeLegacy(items) { localStorage.setItem(LEGACY_KEY, JSON.stringify(items)); }
  function legacyUuid() {
    return 'c_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }
  SC.cart.getItems = function() { return readLegacy(); };
  SC.cart.addItem = function(item) {
    var items = readLegacy();
    var entry = {
      id: legacyUuid(), productHandle: item.productHandle || 'sofa',
      rangeName: item.rangeName || '', rangeKey: item.rangeKey || '',
      size: item.size || '', config: item.config || {},
      configSummary: item.configSummary || [], price: item.price || 0,
      image: item.image || null, shareUrl: item.shareUrl || null,
      quantity: 1, addedAt: new Date().toISOString()
    };
    items.push(entry);
    writeLegacy(items);
    return entry;
  };
  SC.cart.removeItem = function(id) {
    writeLegacy(readLegacy().filter(function(i) { return i.id !== id; }));
  };
  SC.cart.updateQuantity = function(id, qty) {
    var items = readLegacy();
    var item = items.find(function(i) { return i.id === id; });
    if (!item) return;
    if (qty <= 0) items = items.filter(function(i) { return i.id !== id; });
    else item.quantity = qty;
    writeLegacy(items);
  };
  SC.cart.getQuantityOf = function(rangeKey) {
    if (!rangeKey) return 0;
    return readLegacy()
      .filter(function(i) { return i.rangeKey === rangeKey; })
      .reduce(function(sum, i) { return sum + (i.quantity || 1); }, 0);
  };
  SC.cart.hasItem = function(rangeKey) { return SC.cart.getQuantityOf(rangeKey) > 0; };
  SC.cart.clear = function() { writeLegacy([]); };

  /* ── Header badge: unified count ── */
  function updateCartBadge() {
    var count = getTotalCount();
    document.querySelectorAll('[data-sc-cart-badge]').forEach(function(el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ── Mini-cart drawer ── */
  var escBound = false;

  function openDrawer() {
    fetchCart()
      .then(function(cart) { renderDrawer(cart, readSofas(), null); })
      .catch(function() { renderDrawer(null, readSofas(), 'We could not load your basket just now.'); });
  }

  function renderDrawer(cart, sofas, errorMsg) {
    var existing = document.querySelector('.sc-cart-drawer');
    if (existing) existing.parentNode.removeChild(existing);

    var shopItems = (cart && cart.items) || [];
    sofas = sofas || [];
    var totalItems = shopItems.length + sofas.length;
    var hasSofas = sofas.length > 0;

    var itemsHtml = '';

    if (errorMsg && !sofas.length) {
      itemsHtml = '<div class="sc-cart-drawer-empty">' +
        '<p>' + escapeHtml(errorMsg) + '</p>' +
        '<p class="sc-cart-drawer-empty-hint"><a href="/cart">Open the basket page instead</a>.</p>' +
        '</div>';
    } else if (!totalItems) {
      itemsHtml = '<div class="sc-cart-drawer-empty">' +
        '<p>Your basket is empty.</p>' +
        '<p class="sc-cart-drawer-empty-hint">Browse the collection or configure a sofa to get started.</p>' +
        '</div>';
    } else {
      /* Configured sofas first, then Shopify items */
      itemsHtml = sofas.map(renderSofaItem).join('') +
        shopItems.map(renderShopifyItem).join('');
    }

    /* Combined subtotal: Shopify pence + sofa pounds */
    var shopPence = cart ? cart.items_subtotal_price : 0;
    var sofaPounds = sofas.reduce(function(s, i) { return s + (i.price || 0); }, 0);
    var subtotalPounds = (shopPence / 100) + sofaPounds;

    var overlay = document.createElement('div');
    overlay.className = 'sc-cart-drawer';
    overlay.innerHTML = '' +
      '<div class="sc-cart-drawer-scrim"></div>' +
      '<aside class="sc-cart-drawer-panel" role="dialog" aria-modal="true" aria-label="Shopping basket">' +
        '<div class="sc-cart-drawer-head">' +
          '<div class="sc-eyebrow">Your basket</div>' +
          '<h2 class="sc-cart-drawer-title">' +
            (totalItems === 0 ? 'Empty' : totalItems + ' item' + (totalItems === 1 ? '' : 's')) +
          '</h2>' +
          '<button class="sc-cart-drawer-close" type="button" aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="sc-cart-drawer-body">' + itemsHtml + '</div>' +
        (totalItems > 0
          ? '<div class="sc-cart-drawer-foot">' +
              '<div class="sc-cart-drawer-subtotal">' +
                '<span>Subtotal</span>' +
                '<span class="sc-cart-drawer-subtotal-amount">' + moneyWhole(subtotalPounds) + '</span>' +
              '</div>' +
              '<p class="sc-cart-drawer-meta">UK delivery by two-person team, included. Lead times shown at checkout.</p>' +
              /* One sofa per order for now: the draft-order endpoint takes a
                 single configured sofa per call. Say so plainly rather than
                 letting a customer reach checkout and find items missing. */
              (hasSofas && (sofas.length > 1 || shopItems.length > 0)
                ? '<p class="sc-cart-drawer-note" style="font-size:12.5px;line-height:1.5;color:var(--ink-soft);margin:0 0 12px;">' +
                    (sofas.length > 1
                      ? 'Each sofa is ordered separately, so this will check out your ' +
                        (sofas[0].rangeName || 'first sofa') + ' first. The rest stay in your basket. '
                      : '') +
                    (shopItems.length > 0
                      ? 'Your other items check out separately at <a href="/cart" style="color:inherit;text-decoration:underline;">your basket</a>. '
                      : '') +
                    'If you would rather do it all in one go, <a href="/pages/contact" style="color:inherit;text-decoration:underline;">talk to our team</a> and we will sort it for you.' +
                  '</p>'
                : '') +
              (hasSofas
                ? '<button type="button" class="sc-btn sc-btn-primary sc-cart-drawer-checkout" data-sc-checkout-draft>Review &amp; checkout</button>'
                : '<a class="sc-btn sc-btn-primary sc-cart-drawer-checkout" href="/cart">Review &amp; checkout</a>') +
              '<button class="sc-cart-drawer-continue" type="button">Continue browsing</button>' +
            '</div>'
          : '') +
      '</aside>';

    document.body.appendChild(overlay);
    document.body.classList.add('sc-cart-drawer-open');
    requestAnimationFrame(function() { overlay.classList.add('is-visible'); });

    /* Wire close handlers */
    overlay.querySelector('.sc-cart-drawer-scrim').addEventListener('click', closeDrawer);
    overlay.querySelector('.sc-cart-drawer-close').addEventListener('click', closeDrawer);
    var continueBtn = overlay.querySelector('.sc-cart-drawer-continue');
    if (continueBtn) continueBtn.addEventListener('click', closeDrawer);

    /* Wire Shopify item remove / qty */
    overlay.querySelectorAll('[data-sc-cart-remove]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        btn.disabled = true;
        changeLine(btn.getAttribute('data-sc-cart-remove'), 0)
          .then(function(cartNow) { renderDrawer(cartNow, readSofas(), null); })
          .catch(function() { renderDrawer(null, readSofas(), 'We could not update your basket just now.'); });
      });
    });
    overlay.querySelectorAll('[data-sc-cart-qty]').forEach(function(input) {
      input.addEventListener('change', function() {
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) qty = 0;
        /* Below the minimum but not zero: pull it back up to the minimum.
           Zero still removes the line. */
        var min = parseInt(input.getAttribute('data-sc-cart-min'), 10) || 1;
        if (qty > 0 && qty < min) {
          qty = min;
          input.value = min;
        }
        input.disabled = true;
        changeLine(input.getAttribute('data-sc-cart-qty'), qty)
          .then(function(cartNow) { renderDrawer(cartNow, readSofas(), null); })
          .catch(function() { renderDrawer(null, readSofas(), 'We could not update your basket just now.'); });
      });
    });

    /* Wire configured-sofa remove */
    overlay.querySelectorAll('[data-sc-sofa-remove]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        SC.cart._removeConfiguredSofa(btn.getAttribute('data-sc-sofa-remove'));
        /* Re-render with current Shopify cart (avoid re-fetch) */
        renderDrawer(_shopCart, readSofas(), null);
      });
    });

    /* Wire draft-order checkout (when sofas are in the basket) */
    var draftBtn = overlay.querySelector('[data-sc-checkout-draft]');
    if (draftBtn) {
      draftBtn.addEventListener('click', function() {
        draftBtn.disabled = true;
        draftBtn.textContent = 'Processing\u2026';

        /* The draft-order endpoint takes ONE sofa per call, flat, and
           requires price, range and size. It does not read a `sofas`
           array and it ignores Shopify cart items, so we send the
           oldest sofa on its own and leave everything else in the
           basket for a separate checkout. */
        var queue = readSofas();
        var sofa = queue[0];
        if (!sofa) { draftBtn.disabled = false; draftBtn.textContent = 'Review & checkout'; return; }

        var draftPayload = {
          price: sofa.price,
          range: sofa.rangeKey || sofa.range || '',
          size: sofa.size || '',
          fabric: sofa.fabric || '',
          sku: sofa.sku || '',
          /* Extra context for the order line. The endpoint ignores what
             it does not recognise, and accepted these in testing. */
          rangeName: sofa.rangeName || '',
          sizeLabel: sofa.sizeLabel || '',
          fabricName: sofa.fabricName || '',
          cushion: sofa.cushion || '',
          cover: sofa.cover || '',
          piping: sofa.piping || '',
          cushionFill: sofa.cushionFill || '',
          legStyle: sofa.legStyle || '',
          legFinish: sofa.legFinish || '',
          frame: sofa.frame || '',
          image: sofa.image || '',
          shareUrl: sofa.shareUrl || ''
        };

        fetch('https://cdn.sophieconran.com/api/draft-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(draftPayload)
        })
        .then(function(r) {
          return r.text().then(function(t) {
            var data = null;
            try { data = JSON.parse(t); } catch (e) {}
            if (!r.ok) {
              throw new Error('status ' + r.status +
                (data && data.error ? ' \u2014 ' + data.error : ' \u2014 ' + t.slice(0, 200)));
            }
            return data;
          });
        })
        .then(function(data) {
          if (data && data.checkoutUrl) {
            /* Remove only the sofa now on its way to checkout. Anything
               else stays, so a second sofa or a Shopify item is not
               silently lost. */
            writeSofas(readSofas().filter(function(s) { return s.id !== sofa.id; }));
            window.location.href = data.checkoutUrl;
          } else {
            throw new Error('No checkout URL');
          }
        })
        .catch(function(err) {
          console.error('[Draft order]', err && err.message ? err.message : err);
          draftBtn.disabled = false;
          draftBtn.textContent = 'Review & checkout';
          var foot = overlay.querySelector('.sc-cart-drawer-foot');
          if (foot && !foot.querySelector('.sc-cart-drawer-error')) {
            foot.insertAdjacentHTML('afterbegin',
              '<p class="sc-cart-drawer-error" style="color:var(--terracotta);font-size:13px;margin:0 0 12px;">' +
              'Checkout is not available just now. Please <a href="/pages/contact" style="color:inherit;text-decoration:underline;">contact us</a> to place your order.</p>');
          }
        });
      });
    }

    if (!escBound) {
      document.addEventListener('keydown', escListener);
      escBound = true;
    }
  }

  function escListener(e) {
    if (e.key === 'Escape') closeDrawer();
  }

  function closeDrawer() {
    var overlay = document.querySelector('.sc-cart-drawer');
    if (!overlay) return;
    overlay.classList.remove('is-visible');
    document.body.classList.remove('sc-cart-drawer-open');
    if (escBound) {
      document.removeEventListener('keydown', escListener);
      escBound = false;
    }
    setTimeout(function() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 220);
  }

  /* ── Render a configured sofa item ── */
  function renderSofaItem(sofa) {
    var img = sofa.image
      ? '<img src="' + escapeAttr(sofa.image) + '" alt="" loading="lazy" />'
      : '<div class="sc-cart-item-image-fallback"></div>';

    var title = (sofa.rangeName || 'Sofa') + ' ' + (sofa.sizeLabel || sofa.size || '');
    var fabricLine = sofa.fabricName
      ? '<li>Fabric: ' + escapeHtml(sofa.fabricName) + '</li>'
      : '';
    var configLines = (sofa.configSummary || []).map(function(line) {
      return '<li>' + escapeHtml(line.label) + ': ' + escapeHtml(line.value) + '</li>';
    }).join('');

    var priceStr = sofa.price != null ? moneyWhole(sofa.price) : 'Price on request';

    return '' +
      '<article class="sc-cart-item sc-cart-item-sofa" data-sc-cart-sofa="' + escapeAttr(sofa.id) + '">' +
        '<div class="sc-cart-item-image">' + img + '</div>' +
        '<div class="sc-cart-item-body">' +
          '<h3 class="sc-cart-item-name">' + escapeHtml(title) + '</h3>' +
          '<ul class="sc-cart-item-config">' + fabricLine + configLines + '</ul>' +
          '<div class="sc-cart-item-foot">' +
            '<span class="sc-cart-item-qty-label">Qty 1</span>' +
            '<span class="sc-cart-item-price">' + priceStr + '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="sc-cart-item-remove" data-sc-sofa-remove="' + escapeAttr(sofa.id) + '" aria-label="Remove">&times;</button>' +
      '</article>';
  }

  /* ── Minimum order quantity for a Shopify cart line ──
     /cart.js carries no metafields, so the product page stamps the minimum
     onto the line as the hidden property _min_qty. Lines added before that
     property existed simply have no minimum here; the cart page still
     enforces it from the metafield before checkout. */
  function lineMin(item) {
    var props = item && item.properties;
    if (!props) return 1;
    var raw = Array.isArray(props)
      ? (props.filter(function(p) { return p && p[0] === '_min_qty'; })[0] || [])[1]
      : props._min_qty;
    var n = parseInt(raw, 10);
    return (isNaN(n) || n < 1) ? 1 : n;
  }

  /* ── Render a Shopify cart line item ── */
  function renderShopifyItem(item) {
    var img = item.image
      ? '<img src="' + escapeAttr(item.image) + '" alt="" loading="lazy" />'
      : '<div class="sc-cart-item-image-fallback"></div>';

    var title = item.product_title || item.title || '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      title += ' \u00b7 ' + item.variant_title;
    }

    var configLines = '';
    var props = item.properties;
    if (props) {
      var pairs = Array.isArray(props)
        ? props
        : Object.keys(props).map(function(k) { return [k, props[k]]; });
      configLines = pairs
        .filter(function(p) { return p && p[0] && String(p[0]).charAt(0) !== '_' && p[1]; })
        .map(function(p) { return '<li>' + escapeHtml(p[0]) + ': ' + escapeHtml(p[1]) + '</li>'; })
        .join('');
    }

    var unit = money(item.final_price != null ? item.final_price : item.price);
    var line = money(item.final_line_price != null ? item.final_line_price : item.line_price);
    var min = lineMin(item);

    return '' +
      '<article class="sc-cart-item" data-sc-cart-item="' + escapeAttr(item.key) + '">' +
        '<div class="sc-cart-item-image">' + img + '</div>' +
        '<div class="sc-cart-item-body">' +
          '<h3 class="sc-cart-item-name">' + escapeHtml(title) + '</h3>' +
          (configLines ? '<ul class="sc-cart-item-config">' + configLines + '</ul>' : '') +
          '<div class="sc-cart-item-foot">' +
            '<label class="sc-cart-item-qty-label">Qty ' +
              '<input type="number" min="0" max="999" value="' + (item.quantity || 1) +
              '" data-sc-cart-qty="' + escapeAttr(item.key) + '"' +
              ' data-sc-cart-min="' + min + '" />' +
            '</label>' +
            (min > 1
              ? '<span class="sc-cart-item-min" style="font-size:11px;opacity:0.65;margin-left:8px;">Min ' + min + '</span>'
              : '') +
            '<span class="sc-cart-item-price">' +
              (item.quantity > 1 ? unit + ' &times; ' + item.quantity + ' = ' + line : line) +
            '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="sc-cart-item-remove" data-sc-cart-remove="' + escapeAttr(item.key) + '" aria-label="Remove">&times;</button>' +
      '</article>';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  /* ── Header cart icon wiring ── */
  function wireCartIcon() {
    document.addEventListener('click', function(e) {
      var target = e.target.closest && e.target.closest('[data-sc-cart-open]');
      if (!target) return;
      e.preventDefault();
      openDrawer();
    });
    fetchCart().catch(function() { /* offline: badge still reflects sofas */ });
    /* In case fetchCart fails, set badge from sofas alone */
    updateCartBadge();
  }

  SC.cart.openDrawer = openDrawer;
  SC.cart.closeDrawer = closeDrawer;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireCartIcon);
  } else {
    wireCartIcon();
  }
})();
